/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, FormControl, MenuItem, Select, TextField } from '@mui/material';
import React from 'react';
import { useTimesliderTranslation } from '../../../utils/i18n';
import {
  getLargestTimeUnit,
  TimeInMinutes,
} from '../../TimeSlider/timeSliderUtils';

export interface TimeSpanButtonCustomSelectProps {
  timeSliderSpan: number;
  setOpen: (display: boolean) => void;
  onChangeSliderValue: (value: number) => void;
}

export const TimeSpanButtonCustomSelect: React.FC<
  TimeSpanButtonCustomSelectProps
> = ({
  timeSliderSpan,
  setOpen,
  onChangeSliderValue,
}: TimeSpanButtonCustomSelectProps) => {
  const { t } = useTimesliderTranslation();
  const [selectedTimeValue, setSelectedTimeValue] = React.useState(
    timeSliderSpan / getLargestTimeUnit(timeSliderSpan),
  );
  const [selectedTimeResolution, setSelectedTimeResolution] = React.useState(
    getLargestTimeUnit(timeSliderSpan).toString(),
  );
  const [isANumber, setIsANumber] = React.useState(true);

  return (
    <Box>
      <FormControl
        onKeyDown={(event): void => {
          event.stopPropagation();
        }}
        size="small"
        sx={{
          flexDirection: 'row',
          display: 'flex',
        }}
        variant="standard"
      >
        <TextField
          autoFocus
          color={isANumber ? 'primary' : 'warning'}
          label={t('timeslider-span')}
          onBlur={() => {
            onChangeSliderValue(
              selectedTimeValue * Number(selectedTimeResolution),
            );
          }}
          onChange={(e) => {
            if (!isNaN(+e.target.value)) {
              setIsANumber(true);
              setSelectedTimeValue(Number(e.target.value));
            } else {
              setIsANumber(false);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Escape') {
              onChangeSliderValue(
                selectedTimeValue * Number(selectedTimeResolution),
              );
              setOpen(false);
            }
          }}
          size="small"
          slotProps={{
            htmlInput: {
              maxLength: 4,
              'aria-label': 'custom-input',
            },
          }}
          value={Math.round(selectedTimeValue * 100) / 100}
          variant="standard"
        />
        <Box sx={{ alignContent: 'end' }}>
          <Select
            aria-label="time-resolution-selector"
            defaultValue="h"
            onChange={(e) => {
              setSelectedTimeResolution(e.target.value);
              onChangeSliderValue(selectedTimeValue * Number(e.target.value));
              setOpen(false);
            }}
            size="small"
            tabIndex={-1}
            value={selectedTimeResolution}
          >
            <MenuItem value={TimeInMinutes.YEAR}>{t('years')}</MenuItem>
            <MenuItem value={TimeInMinutes.MONTH}>{t('months')}</MenuItem>
            <MenuItem value={TimeInMinutes.WEEK}>{t('weeks')}</MenuItem>
            <MenuItem value={TimeInMinutes.DAY}>{t('days')}</MenuItem>
            <MenuItem value={TimeInMinutes.HOUR}>{t('hours')}</MenuItem>
            <MenuItem value={TimeInMinutes.MINUTE}>{t('minutes')}</MenuItem>
          </Select>
        </Box>
      </FormControl>
    </Box>
  );
};
